import { event, engagement } from '@blocket/tracking-pulse'
import gtm from '@blocket/tracking-gtm'
import log from 'sentry/log'
import { camelCase } from 'lodash'
import { BANKID, SMS } from 'constants/verificationTypes'

const getAdIdFromChannel = (selectedChannel) => JSON.parse(selectedChannel.data).ad.id
const formatObjectId = (id) => `chat/${camelCase(id)}`
const formatOriginOfChannel = (selectedChannel) =>
  selectedChannel?.url
    ? { id: selectedChannel?.url, type: 'Conversation' }
    : { id: getAdIdFromChannel(selectedChannel), type: 'ClassifiedsAd' }

const errorPrefix = 'Chat tracking error'

export const chatVerificationPromptView = ({ type, selectedChannel }) => {
  const suffix = type === BANKID ? '_bank_id' : type === SMS ? '_sms' : ''
  const origin = formatOriginOfChannel(selectedChannel)

  try {
    event({
      type: 'View',
      name: 'View chat verification prompt',
      object: {
        type: 'Page',
        id: formatObjectId(`conversation_verification_prompt${suffix}`),
      },
      origin,
    })
  } catch (error) {
    log.message(`${errorPrefix}: chatVerificationPromptView ${error}`)
  }
}

export const trackTransactionInfoView = ({ selectedChannel, handlerId, trackingId, role }) => {
  const adId = getAdIdFromChannel(selectedChannel)
  event({
    type: 'View',
    object: {
      id: `sdrn:blocket:chat:transaction_info:${handlerId}:${trackingId}`,
      name: 'View transaction info',
      type: 'Content',
      items: [
        { type: 'ClassifiedAd', id: adId },
        { type: 'Conversation', id: selectedChannel.url },
      ],
      'spt:custom': {
        role,
      },
    },
  })
}

export const trackTransactionInfoClick = ({ selectedChannel, handlerId, trackingId, role }) => {
  const adId = getAdIdFromChannel(selectedChannel)
  engagement({
    action: 'Click',
    object: {
      id: `sdrn:blocket:chat:transaction_info:${handlerId}:${trackingId}`,
      name: 'Clicked on transaction info button',
      type: 'UIElement',
      items: [
        { type: 'ClassifiedAd', id: adId },
        { type: 'Conversation', id: selectedChannel.url },
      ],
      'spt:custom': {
        role,
      },
    },
  })
}

export const chatVerificationPromptEngagement = ({ type, selectedChannel }) => {
  const suffix = type === BANKID ? 'bank_id' : type === SMS && 'sms'
  const origin = formatOriginOfChannel(selectedChannel)
  try {
    engagement({
      action: 'Click',
      name: 'Click chat verification prompt button',
      object: {
        type: 'UIElement',
        id: formatObjectId(`conversation_verification_prompt_verify_by_${suffix}`),
      },
      origin,
    })
  } catch (error) {
    log.message(`${errorPrefix} chatVerificationPromptEngagement: ${error}`)
  }
}

export const trackCustomerServiceLink = (channelUrl) => {
  try {
    event({
      type: 'Engagement',
      action: 'Click',
      object: {
        type: 'UIElement',
        id: `conversation:${channelUrl}`,
      },
    })
  } catch (e) {
    log.error(`${errorPrefix}: trackCustomerServiceLink`, e)
  }
}

export const trackReportUserInvalidDescription = () => {
  try {
    event({
      type: 'View',
      object: {
        type: 'Error',
        id: 'report_user_error',
        cause: [
          {
            description: 'report_user_description_required',
          },
        ],
      },
    })
  } catch (e) {
    log.error(`${errorPrefix}: trackReportUserInvalidDescription`, e)
  }
}

export const trackModalView = ({
  objectId,
  objectType = 'Page',
  objectCategory,
  sptCustom,
  intent,
  eventName,
}) => {
  try {
    event({
      type: 'View',
      ...(intent && { intent }),
      ...(eventName && { name: eventName }),
      object: {
        type: objectType,
        id: objectId,
        ...(objectCategory && { category: objectCategory }),
        ...(sptCustom && { 'spt:custom': sptCustom }),
      },
    })
  } catch (e) {
    log.error(`${errorPrefix}: ${objectId}`, e)
  }
}

export const trackingWarningModalAction = (type, channelUrl, element) => {
  try {
    // shouldn't send tracking name when type is send_message_chat
    type = type === 'send_message_chat' ? '' : type
    event({
      type: 'Engagement',
      ...(type && { name: type }),
      object: {
        id: `sdrn:blocket:conversation:${channelUrl}:element:safety_tips_${element}_clicked`,
        type: 'UIElement',
      },
    })
  } catch (e) {
    log.error(`${errorPrefix}: trackingWarningModalAction`, e)
  }
}

export const trackingWarningModalReadMoreLink = (type, currentUrl) => {
  try {
    event({
      type: 'Engagement',
      name: type,
      object: {
        id: `sdrn:blocket:conversation:${currentUrl}:element:safety_tips_risky_message_warning_clicked`,
        type: 'UIElement',
      },
    })
  } catch (e) {
    log.error(`${errorPrefix}: trackingWarningModalReadMoreLink`, e)
  }
}

export const trackingWarningModalReadMoreBox = (currentUrl) => {
  try {
    event({
      type: 'Engagement',
      object: {
        id: `sdrn:blocket:conversation:${currentUrl}:element:safety_tips_read_more_clicked`,
        type: 'UIElement',
      },
    })
  } catch (e) {
    log.error(`${errorPrefix}: trackingWarningModalReadMoreBox`, e)
  }
}

export const trackingAdReplyFormError = (
  adId,
  sbErrorObj = 'Error sending message',
  objectId = 'ad_reply_form_error',
) => {
  const errorDescription =
    sbErrorObj === 'receiver is blocked' ? 'Receiver is blocked' : sbErrorObj.data
  try {
    event({
      type: 'Error',
      name: 'Error sent message from Ad reply form',
      object: {
        type: 'Error',
        id: formatObjectId(objectId),
        items: [{ id: adId, type: 'ClassifiedAd' }],
        cause: [{ description: errorDescription }],
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingAdReplyFormError ${error}`)
  }
}

// When users view a conversation
export const userViewsChatConversation = ({ selectedChannel }) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)
    event({
      type: 'View',
      name: 'View conversation',
      object: {
        type: 'Conversation',
        id: selectedChannel.url,
        items: [{ type: 'ClassifiedAd', id: adId }],
        inReplyTo: {
          type: 'ClassifiedAd',
          id: adId,
        },
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: userViewsChatConversation ${error}`)
  }
}

// When users view the conversation list
export const trackingUserViewsConversationList = () => {
  try {
    // this gtm.pageView tracking is specifically asked for from marknad
    gtm.pageView({ id: 'conversation-list' })

    event({
      type: 'View',
      name: 'View conversation list',
      object: {
        type: 'Page',
        id: formatObjectId('conversation_list'),
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingUserViewsConversationList ${error}`)
  }
}

// A 3rd party advertisement (tip) is loaded
export const tracking3dPartyAdLoaded = ({ tip, selectedChannel }) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)
    event({
      type: 'View',
      name: 'View messaging tip',
      object: {
        id: formatObjectId(`messaging_tip:${tip.id}`),
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: tracking3dPartyAdLoaded ${error}`)
  }
}

// The user clicks a 3rd party advertisement (tip) in a channel
export const tracking3dPartyAdClick = ({ tip, selectedChannel }) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)
    engagement({
      action: 'Click',
      name: 'Clicked on chat tip',
      object: {
        id: formatObjectId(`messaging_tip:${tip.id}`),
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: tracking3dPartyAdClick ${error}`)
  }
}

// Click system message cta button (it's the extension message in the chat conversation)
export const trackingExtensionIntegrationCTA = ({
  extensionData,
  extensionAction,
  selectedChannel,
  role,
}) => {
  // They keep changing the name of the field, but we know that it only contains one field so lets take that one.
  const { extensionName } = extensionData[Object.keys(extensionData)[0]]
  const { url, label } = extensionAction
  const extensionCtaUrl = new URL(url)
  const extensionCtaPath = extensionCtaUrl.pathname + extensionCtaUrl.search

  try {
    const adId = getAdIdFromChannel(selectedChannel)
    engagement({
      action: 'Click',
      name: 'Clicked on system message button',
      object: {
        id: formatObjectId(`systemMessage:${extensionName}`),
        name: `Clicked on system message button with label "${label}"`,
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
        'spt:custom': {
          role,
        },
      },
      target: {
        id: extensionCtaPath,
        type: 'Page',
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingExtensionIntegrationCTA ${error}`)
  }
}

// View extension entrance (button in top of channel, before it was an icon therefore the name 'entrance')
export const trackingExtensionEntranceView = (extensionData, selectedChannel, role) => {
  try {
    const trackingData =
      extensionData.activeExtensions?.length > 0
        ? extensionData.activeExtensions[0]
        : extensionData.extensions[0]
    const adId = getAdIdFromChannel(selectedChannel)
    const { name, id, state } = trackingData
    const hasActionButton = trackingData?.action
    const type = hasActionButton ? 'action' : 'status'

    event({
      type: 'View',
      name: 'View extension entrance',
      object: {
        id: `chat/extension:${id}:${state}:${type}`,
        name: `View extension entrance ${name}`,
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
        'spt:custom': {
          role,
        },
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingExtensionEntranceView ${error}`)
  }
}

// Click extension entrance (button in top of channel, before it was an icon therefore the name entrance)
export const trackingExtensionEntranceClick = (extensionData, selectedChannel, role) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)
    const { name, id, state } = extensionData
    const hasActionButton = extensionData?.action
    const type = hasActionButton ? 'action' : 'status'

    const startUrl = extensionData?.action ? extensionData.action.url : extensionData.startUrl
    const extensionUrl = new URL(startUrl)
    const extensionLinkPath = extensionUrl.pathname + extensionUrl.search

    engagement({
      action: 'Click',
      name: 'Clicked on extension entrance',
      object: {
        id: `chat/extension:${id}:${state}:${type}`,
        name: `Clicked on extension entrance ${name}`,
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
        'spt:custom': {
          role,
        },
      },
      target: {
        id: extensionLinkPath,
        type: 'Page',
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingExtensionInputClickTrigger ${error}`)
  }
}

// The user blocks the other user in a channel
export const trackingUserBlocksUser = (selectedChannel, role) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)

    engagement({
      action: 'Click',
      name: 'Clicked on block user button',
      object: {
        id: formatObjectId('block_user'),
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
        'spt:custom': {
          role,
        },
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingUserBlocksUser ${error}`)
  }
}

// The user un-blocks the other user in a channel
export const trackingUserUnBlocksUser = (selectedChannel, role) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)

    engagement({
      action: 'Click',
      name: 'Clicked on unblock user button',
      object: {
        id: formatObjectId('unblock_user'),
        type: 'UIElement',
        'spt:custom': {
          role,
        },
      },
      items: [
        { type: 'ClassifiedAd', id: adId },
        { type: 'Conversation', id: selectedChannel.url },
      ],
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingUserUnBlocksUser ${error}`)
  }
}

// The user clicks the Ad info in a channel
export const trackingUserClicksAdInfo = (selectedChannel, ad) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)

    engagement({
      action: 'Click',
      name: 'Clicked on ad info link in conversation',
      object: {
        id: formatObjectId('conversation_ad_info'),
        type: 'UIElement',
        items: [
          { type: 'ClassifiedAd', id: adId },
          { type: 'Conversation', id: selectedChannel.url },
        ],
      },
      target: {
        id: ad.url,
        type: 'Page',
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingUserClicksAdInfo ${error}`)
  }
}

// User sent a message in the chat
export const userSentChatMessage = ({ selectedChannel, isFirstMessage }) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)

    // this gtm.event tracking is specifically asked for from marknad
    gtm.event({ adId, id: 'contact-link' })

    event({
      type: 'Send',
      name: 'Sent message from the chat',
      object: {
        type: 'Message',
        id: formatObjectId('message_sent'),
        inReplyTo: {
          // type Message must include inReplyTo
          type: 'ClassifiedAd',
          id: adId,
        },
        items: [{ type: 'Conversation', id: selectedChannel.url }],
        isFirstMessage,
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: userSentChatMessage ${error}`)
  }
}

// The user sends an file (images included) from a channel
export const trackingUserSentFile = ({ selectedChannel }) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)

    event({
      type: 'Send',
      name: 'Sent file from the chat',
      object: {
        id: formatObjectId('file_sent'),
        type: 'Message',
        inReplyTo: {
          // type Message must include inReplyTo
          type: 'ClassifiedAd',
          id: adId,
        },
        items: [{ type: 'Conversation', id: selectedChannel.url }],
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackingUserSentFile ${error}`)
  }
}

// The user gets and error when sending a message in a specific channel
export const trackingUserSendMessageError = ({ selectedChannel, error }) => {
  try {
    const adId = getAdIdFromChannel(selectedChannel)

    event({
      type: 'Error',
      name: 'Error send message from chat',
      object: {
        type: 'Error',
        id: formatObjectId('error_send_message'),
        items: [{ id: adId, type: 'ClassifiedAd' }],
        cause: [{ description: error || 'Error sending message' }],
      },
    })
  } catch (err) {
    log.message(`${errorPrefix}: trackingUserSendMessageError ${error}`)
  }
}

export const trackActivateEmailCopy = () => {
  try {
    event({
      type: 'Engagement',
      name: 'Click chat modal activate email copy',
      action: 'Clicked',
      object: {
        type: 'UIElement',
        id: 'chat_modal_activate_email_copy',
      },
    })
  } catch (error) {
    log.message(`${errorPrefix}: trackActivateEmailCopy ${error}`)
  }
}
